import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { PaymentsService } from '../../services/payments/payments.service';
import { BillingService } from "../../services/billing/billing.service";
import { AddFilesComponent } from '../../components/marketing/add-files/add-files.component';
import Swal from 'sweetalert2';
import { ProspectsService } from "../../services/prospects/prospects.service";

@Component({
  selector: 'app-payments-history',
  templateUrl: './payments-history.component.html',
  styleUrls: ['./payments-history.component.scss']
})
export class PaymentsHistoryComponent implements OnInit {

  public history: any[] = [];
  public loading = false;

  paymentEditingMap: { [key: string]: boolean } = {};
  cycleEditingMap: { [key: string]: boolean } = {};

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private prospectService: ProspectsService,
    private billingService: BillingService,
    private paymentsService: PaymentsService,
    private dialogRef: MatDialogRef<PaymentsHistoryComponent>,
    private dialog: MatDialog
  ) {}

  async ngOnInit() {
    this.loading = true;

    try {
      this.history = await this.paymentsService.getBillingHistory(
        this.data.clientId
      );

      this.history.forEach(cycle => {

        cycle.originalStartDate = cycle.startDate;

        cycle.payments.forEach(payment => {
          payment.originalMonto = payment.monto;
        });
      });

    } catch (error) {
      Swal.fire(
        "Error",
        "No se pudo cargar el historial",
        "error"
      );
    }

    this.loading = false;
  }

  async savePayment(payment: any) {
    try {
      const montoChanged =
        Number(payment.monto) !==
        Number(payment.originalMonto || payment.monto);

      let changeReason = null;

      if (montoChanged) {
        const result = await Swal.fire({
          title: "Cambio de monto",
          text: "Debes justificar el cambio de precio",
          input: "textarea",
          inputPlaceholder: "Ej: descuento, cambio de plan, negociación...",
          inputAttributes: {
            maxlength: "300"
          },
          showCancelButton: true,
          confirmButtonText: "Guardar",
          cancelButtonText: "Cancelar",
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return "La justificación es obligatoria";
            }
            if (value.trim().length < 5) {
              return "Debe tener al menos 5 caracteres";
            }
            return null;
          }
        });

        if (!result.isConfirmed) {
          return;
        }

        changeReason = result.value.trim();
      }

      await this.paymentsService.updatePayment(
        payment._id,
        {
          monto: Number(payment.monto),
          status: payment.status,
          invoiceNumber: payment.invoiceNumber,
          notes: payment.notes || '',
          formaPago: payment.formaPago || '',
          contacto: payment.contacto || '',
          changeReason: montoChanged ? changeReason : null,
          previousAmount: montoChanged ? Number(payment.originalMonto || 0) : null
        }
      );

      if (montoChanged) {
        payment.previousAmount = payment.originalMonto;
        payment.priceChangedAt = new Date();
        payment.changeReason = changeReason;
        payment.originalMonto = payment.monto;
      }

      this.paymentEditingMap[String(payment._id)] = false;

      Swal.fire(
        "OK",
        "Pago actualizado",
        "success"
      );

    } catch (error) {
      Swal.fire(
        "Error",
        error?.error?.error || "No se pudo actualizar el pago",
        "error"
      );
    }
  }

  getCycleTotalAmount(cycle: any): number {
    if (!cycle?.payments) return 0;
    return cycle.payments
      .filter(p => p.status === 'pagado' || p.status === 'facturado')
      .reduce((sum, p) => sum + Number(p.monto || 0), 0);
  }

  getCycleTotalExtraHours(cycle: any): number {
    if (!cycle?.payments) return 0;
    return cycle.payments
      .filter(p => p.type === 'extra' && (p.status === 'pagado' || p.status === 'facturado'))
      .reduce((sum, p) => sum + Number(p.extraHours || 0), 0);
  }

  getCycleTotalExtraMinutes(cycle: any): number {
    if (!cycle?.payments) return 0;
    return cycle.payments
      .filter(p => p.type === 'extra' && (p.status === 'pagado' || p.status === 'facturado'))
      .reduce((sum, p) => sum + Number(p.extraMinutes || 0), 0);
  }

  getPaymentTypeName(payment: any, cycle?: any): string {
    if (payment?.type === 'extra') {
      return 'Excedente';
    }
    return cycle?.cycleType || 'Renovación';
  }

  async uploadFile(payment: any, type: string) {
    try {
      const result = await this.dialog.open(
        AddFilesComponent,
        {
          data: { title: type },
          height: '60vh'
        }
      ).beforeClosed().toPromise();

      if (!result) return;

      let update: any = {};

      if (type === "Factura") {
        update.invoiceFile = result;
        payment.invoiceFile = result;
      }

      if (type === "Comprobante de pago") {
        update.newPaymentProof = result;

        if (!payment.paymentProofFiles) {
          payment.paymentProofFiles = [];
        }

        payment.paymentProofFiles.push({
          ...result,
          uploadedAt: new Date()
        });
      }

      await this.paymentsService.updatePayment(payment._id, update);

      Swal.fire("OK", "Archivo subido correctamente", "success");

    } catch (error) {
      Swal.fire("Error", "No se pudo subir el archivo", "error");
    }
  }

  async replaceProof(payment: any, index: number) {
    try {
      const result = await this.dialog.open(
        AddFilesComponent,
        {
          data: { title: "Comprobante de pago" },
          height: '60vh'
        }
      ).beforeClosed().toPromise();

      if (!result) return;

      const updatedFiles = [...payment.paymentProofFiles];
      updatedFiles[index] = {
        ...result,
        uploadedAt: new Date()
      };

      await this.paymentsService.updatePayment(payment._id, {
        paymentProofFiles: updatedFiles
      });

      payment.paymentProofFiles = updatedFiles;

      Swal.fire("OK", "Comprobante reemplazado correctamente", "success");

    } catch (error) {
      Swal.fire("Error", "No se pudo reemplazar el comprobante", "error");
    }
  }

  async removeInvoice(payment: any) {
    try {
      const confirm = await Swal.fire({
        title: "Eliminar factura",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!confirm.isConfirmed) return;

      await this.paymentsService.updatePayment(payment._id, { invoiceFile: null });
      payment.invoiceFile = null;

      Swal.fire("OK", "Factura eliminada", "success");

    } catch (error) {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  }

  async removeProof(payment: any, index: number) {
    try {
      const confirm = await Swal.fire({
        title: "Eliminar comprobante",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!confirm.isConfirmed) return;

      const updatedFiles = [...payment.paymentProofFiles];
      updatedFiles.splice(index, 1);

      await this.paymentsService.updatePayment(payment._id, {
        paymentProofFiles: updatedFiles
      });

      payment.paymentProofFiles = updatedFiles;

      Swal.fire("OK", "Comprobante eliminado", "success");

      this.dialogRef.close({ refresh: true });

    } catch (error) {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  }

  async deleteCycle(cycle: any) {
    try {
      const confirm = await Swal.fire({
        title: "Eliminar ciclo",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!confirm.isConfirmed) return;

      await this.billingService.deleteCycle(cycle.cycleId);

      this.history = this.history.filter(c => c.cycleId !== cycle.cycleId);

      Swal.fire("OK", "Ciclo eliminado", "success");

      this.dialogRef.close({ refresh: true });

    } catch (error) {
      Swal.fire(
        "Error",
        error?.error?.error || "No se pudo eliminar",
        "error"
      );
    }
  }

  isPaymentEditing(id: any): boolean {
    const key = String(id);
    return this.paymentEditingMap[key] === true;
  }

  togglePaymentEdit(id: any): void {
    const key = String(id);
    const wasOpen = this.paymentEditingMap[key] === true;

    Object.keys(this.paymentEditingMap).forEach(k => {
      this.paymentEditingMap[k] = false;
    });

    if (!wasOpen) {
      this.paymentEditingMap[key] = true;
    }
  }

  isCycleEditing(id: any): boolean {
    const key = this.buildCycleKey(id);
    return this.cycleEditingMap[key] === true;
  }

  toggleCycleEdit(id: any): void {
    const key = this.buildCycleKey(id);
    const wasOpen = this.cycleEditingMap[key] === true;

    Object.keys(this.cycleEditingMap).forEach(k => {
      this.cycleEditingMap[k] = false;
    });

    if (!wasOpen) {
      this.cycleEditingMap[key] = true;
    }
  }

  trackByCycle(index: number, item: any) {
    return item._id;
  }

  trackByPayment(index: number, item: any) {
    return item._id;
  }

  viewFile(url: string) {
    window.open(url, '_blank');
  }

  downloadFile(url: string, fileType: string) {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `${fileType}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        Swal.fire("Error", "No se pudo descargar el archivo", "error");
      });
  }

  async saveCycle(cycle: any) {
    try {
      const startDateChanged = cycle.startDate !== cycle.originalStartDate;

      let resetType = cycle.resetType || "monthly";

      if (startDateChanged) {
        const earlyResetAlert = await Swal.fire({
          title: "¿Renovación anticipada?",
          text: "Se detectó un cambio en la fecha de inicio del ciclo. ¿Es una renovación anticipada?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, es anticipada",
          cancelButtonText: "No",
          allowOutsideClick: false
        });

        resetType = earlyResetAlert.isConfirmed ? "early_reset" : "monthly";
      }

      cycle.endDate = this.calculateEndDate(cycle.startDate);

      await this.billingService.updateBillingCycle(
        cycle.cycleId,
        { 
          startDate: cycle.startDate,
          endDate: cycle.endDate,
          resetType: resetType 
        }
      );

      cycle.resetType = resetType;
      cycle.originalStartDate = cycle.startDate;

      Swal.fire("OK", "Ciclo actualizado correctamente", "success");
      this.toggleCycleEdit(cycle.cycleId);

      this.dialogRef.close({ refresh: true });

    } catch (error) {
      Swal.fire("Error", "No se pudo actualizar el ciclo", "error");
    }
  }

  private buildCycleKey(id: any): string {
    if (!id) return '';
    return String(id);
  }

  getStatusClass(status: string) {
    return {
      'status-pendiente': status === 'pendiente',
      'status-pagado': status === 'pagado',
      'status-facturado': status === 'facturado'
    };
  }

  calculateEndDate(startDate: any) {
    if (!startDate) return null;
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 1);
    return endDate;
  }

  close() {
    this.dialogRef.close();
  }
}